import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

type Definition = monaco.languages.IMonarchLanguage;

type Theme = monaco.editor.IStandaloneThemeData;

/* eslint-disable no-useless-escape */
class EditorConfiguration {
    static definition(): Definition {
        return {
            defaultToken: 'invaid',

            keywords: [
                'and',
                'case',
                'coalesce',
                'default',
                'else',
                'end',
                'for',
                'if',
                'in',
                'in_map',
                'is',
                'not',
                'null',
                'or',
                'switch',
                'then',
                'when',
                'while'
            ],

            typeKeywords: [],

            operators: [
                '=',
                '>',
                '<',
                '!',
                '==',
                '<=',
                '>=',
                '!=',
                '&&',
                '||',
                '++',
                '--',
                '+',
                '-',
                '*',
                '/',
                '&',
                '|',
                '+=',
                '-=',
                '*=',
                '/='
            ],

            // we include these common regular expressions
            symbols: /[=><!~?:&|+\-*\/^%]+/,
            escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,

            tokenizer: {
                root: [
                    [/true|false/, 'boolean'],

                    [/[a-zA-Z_$][\w$]*(?=\()/, 'function'],

                    [
                        /[a-zA-Z_$][\w$]*/,
                        {
                            cases: {
                                '@typeKeywords': 'keyword',
                                '@keywords': 'keyword',
                                '@default': 'identifier'
                            }
                        }
                    ],

                    [/[A-Z][\w$]*/, 'type.identifier'], // to show class names nicely

                    // whitespace
                    { include: '@whitespace' },

                    // delimiters and operators
                    [/[{}()\[\]]/, 'brackets'],
                    [/[<>](?!@symbols)/, '@brackets'],
                    [
                        /@symbols/,
                        {
                            cases: {
                                '@operators': 'operator',
                                '@default': ''
                            }
                        }
                    ],

                    // numbers
                    [/\d*\.\d+([eE][\-+]?\d+)?[fFdD]?/, 'number.float'],
                    [/0[xX][0-9a-fA-F_]*[0-9a-fA-F][Ll]?/, 'number.hex'],
                    [/0[0-7_]*[0-7][Ll]?/, 'number.octal'],
                    [/0[bB][0-1_]*[0-1][Ll]?/, 'number.binary'],
                    [/\d+[lL]?/, 'number'],

                    // delimiter: after number because of .\d floats
                    [/[;,.]/, 'delimiter'],

                    // strings
                    [/"([^"\\]|\\.)*$/, 'string.invalid'], // non-teminated string
                    [/"/, 'string', '@string'],

                    // characters
                    [/'[^\\']'/, 'string'],
                    [/(')(@escapes)(')/, ['string', 'string.escape', 'string']],
                    [/'/, 'string.invalid']
                ],

                whitespace: [
                    [/[ \t\r\n]+/, 'white'],
                    [/\/\*/, 'comment', '@comment'],
                    [/\/\+/, 'comment', '@comment'],
                    [/\/\/.*$/, 'comment']
                ],

                comment: [
                    [/[^\/*]+/, 'comment'],
                    [/\/\+/, 'comment', '@push'],
                    [/\/\*/, 'comment.invalid'],
                    ['\\*/', 'comment', '@pop'],
                    ['\\+/', 'comment', '@pop'],
                    [/[\/*]/, 'comment']
                ],

                string: [
                    [/[^\\"]+/, 'string'],
                    [/@escapes/, 'string.escape'],
                    [/\\./, 'string.escape.invalid'],
                    [/"/, 'string', '@pop']
                ]
            }
        };
    }

    static theme(): Theme {
        return {
            base: 'vs',
            inherit: true,
            colors: {},
            rules: [
                { token: 'keyword', foreground: '#FF6600', fontStyle: 'bold' },
                { token: 'identifier', foreground: '#FF0000' },
                { token: 'comment', foreground: '#999999' },
                { token: 'boolean', foreground: '#00FF64' },
                { token: 'brackets', foreground: '#6400FF', fontStyle: 'bold' },
                { token: 'function', foreground: '#0080FF' },
                { token: 'operator', foreground: '#800080' },
                { token: 'number', foreground: '#009966' },
                { token: 'string', foreground: '#804000' },
                { token: 'variable', foreground: '#006699' }
            ]
        };
    }
}

export { EditorConfiguration };
